// The one answer to "how high is the floor here" (CIN-15126). Seats stand on their tier, and so must
// everything else that stands on the floor: room objects, railings, the guest map and the studio all ask
// this module instead of trusting a stored height. Plan space (mm, y toward the screen, rotation in
// degrees clockwise from above); heights are mm above the room's base floor.
// Pure: relative imports only.
import { type PlanPoint, type TierModel, tierHeightAt } from './tiers.ts';

/** A plan rectangle: center, size and turn, as a seat or a room object stores it. */
export type Footprint = { x: number; y: number; width: number; depth: number; rotation: number };

/** Samples stay this far inside the footprint's edge, so a wall-thin object on a tier's border still finds the tier. */
const EDGE_INSET = 0.9;

/**
 * Height of the surface a person or object stands on at a plan point: the tier top or the stair tread
 * there, 0 on the base floor (in front of the first riser, on the flat part of the room, outside it).
 */
export function floorHeightAt(model: TierModel, point: PlanPoint, level = 'main'): number {
    return tierHeightAt(model, point, level);
}

/** The footprint's center, corners and edge midpoints (a 3 x 3 grid), pulled slightly inside the edge. */
export function footprintSamples(footprint: Footprint): PlanPoint[] {
    const radians = (footprint.rotation * Math.PI) / 180;
    const sin = Math.sin(radians);
    const cos = Math.cos(radians);
    const halfWidth = (footprint.width / 2) * EDGE_INSET;
    const halfDepth = (footprint.depth / 2) * EDGE_INSET;
    const samples: PlanPoint[] = [];
    for (const lx of [-halfWidth, 0, halfWidth]) {
        for (const ly of [-halfDepth, 0, halfDepth]) {
            // Clockwise turn seen from above with y toward the screen, the same as a seat's front vector.
            samples.push({ x: footprint.x + lx * cos + ly * sin, y: footprint.y - lx * sin + ly * cos });
        }
    }
    return samples;
}

/**
 * Height of the highest surface of `level` ('main' or a balcony's id) under any part of the footprint. An object that spans a riser stands on
 * the upper level (never buried in it) and only its overhang over the lower level is left in the air.
 */
export function footprintFloorHeight(model: TierModel, footprint: Footprint, level = 'main'): number {
    if (model.tiers.length === 0 && !model.levels?.length) return 0;
    let height = 0;
    for (const sample of footprintSamples(footprint)) {
        height = Math.max(height, floorHeightAt(model, sample, level));
    }
    return height;
}
